/**
 * Tooltip system ported from the designer prototype.
 * Warm/cold delay pattern (CapCut-style):
 * - First tooltip appears after 400ms
 * - While warm (within 350ms of last hide), next tooltip slides in immediately
 * - Content swaps with crossfade when gliding between targets
 */
import { gsap, D } from "./editor_animations"

const CLS_SIMPLE = "rounded-md bg-[#171717] px-2 py-1 text-xs font-medium text-white shadow-lg"
const CLS_RICH = "w-64 rounded-xl bg-surface_0 p-2 sb-modal"

export class TooltipManager {
  constructor(containerEl, innerEl) {
    this._container = containerEl
    this._inner = innerEl
    this._timer = null
    this._warm = false
    this._warmTimer = null
    this._shown = false
    this._key = null
    this._qx = gsap.quickTo(containerEl, "x", { duration: D(0.22), ease: "power3.out" })
    this._qy = gsap.quickTo(containerEl, "y", { duration: D(0.22), ease: "power3.out" })
  }

  /** Show tooltip for an element with data-tip or data-tip-title attributes */
  showFor(target) {
    const key = target.dataset.tipTitle || target.dataset.tip
    if (!key) return

    const r = target.getBoundingClientRect()

    // Same target → just reposition
    if (this._shown && key === this._key) {
      this._place(r.left + r.width / 2 - this._inner.offsetWidth / 2, r.top - this._inner.offsetHeight - 8)
      return
    }

    const isSwap = this._shown
    this._key = key

    gsap.killTweensOf(this._inner)
    this._inner.innerHTML = this._content(target)

    const x = r.left + r.width / 2 - this._inner.offsetWidth / 2
    const y = r.top - this._inner.offsetHeight - 8

    if (isSwap) {
      this._place(x, y)
      gsap.fromTo(this._inner, { autoAlpha: 0.4 }, { autoAlpha: 1, duration: D(0.18), ease: "power2.out" })
    } else {
      this._shown = true
      this._place(x, y, true)
      gsap.fromTo(this._inner,
        { autoAlpha: 0, y: 6, scale: 0.96 },
        { autoAlpha: 1, y: 0, scale: 1, duration: D(0.28), ease: "expo.out" }
      )
    }
  }

  /** Show a drag tooltip (plain text at a position) */
  showDrag(text, pos) {
    this._drag = true
    this._inner.className = CLS_SIMPLE
    this._inner.textContent = text
    this._key = null

    if (this._shown) {
      // Déjà là : son fondu d'entrée continue — appelée à chaque mouvement, la couper la
      // laisserait à mi-opacité
      this._place(pos.x, pos.y)
    } else {
      gsap.killTweensOf(this._inner)
      this._shown = true
      this._place(pos.x, pos.y, true)
      gsap.set(this._inner, { y: 0, scale: 1 })
      gsap.to(this._inner, { autoAlpha: 1, duration: D(0.12), overwrite: "auto" })
    }
  }

  /** Le texte d'un geste, centré au-dessus d'une boîte (la barre d'un calque qu'on tire) */
  showDragAbove(text, rect) {
    this.showDrag(text, { x: 0, y: 0 })
    this._place(
      rect.left + rect.width / 2 - this._inner.offsetWidth / 2,
      rect.top - this._inner.offsetHeight - 6,
    )
  }

  hide() {
    this._drag = false
    if (!this._shown) return
    this._shown = false
    this._key = null
    this._warm = true
    clearTimeout(this._warmTimer)
    this._warmTimer = setTimeout(() => { this._warm = false }, 350)
    gsap.killTweensOf(this._inner)
    gsap.to(this._inner, { autoAlpha: 0, scale: 0.98, duration: D(0.16), ease: "power2.in" })
  }

  /** Handle mouseover event — call from a delegated listener */
  onMouseOver(target) {
    // Un geste l'affiche (showDrag) : le survol n'y touche pas — la timeline se reconstruit
    // sous le pointeur, chaque nouvel élément survolé la cacherait
    if (this._drag) return
    const t = target.closest("[data-tip],[data-tip-title]")
    clearTimeout(this._timer)
    if (!t) { this.hide(); return }
    if (this._shown || this._warm) {
      if (!this._shown) this._shown = true
      this.showFor(t)
    } else {
      this._timer = setTimeout(() => this.showFor(t), 400)
    }
  }

  onPointerDown() {
    clearTimeout(this._timer)
    this.hide()
  }

  _content(target) {
    if (target.dataset.tipTitle) {
      this._inner.className = CLS_RICH
      return `<span class="block px-1 py-1">
        <span class="block text-sm font-semibold text-brand_black">${target.dataset.tipTitle}</span>
        ${target.dataset.tipDesc ? `<span class="mt-0.5 block text-xs leading-relaxed text-[#666]">${target.dataset.tipDesc}</span>` : ""}
      </span>`
    }
    this._inner.className = CLS_SIMPLE
    return target.dataset.tip
  }

  _place(x, y, instant = false) {
    const lx = Math.min(window.innerWidth - this._inner.offsetWidth - 8, Math.max(8, x))
    const ly = Math.max(8, y)
    if (instant) {
      gsap.set(this._container, { x: lx, y: ly })
      this._qx(lx)
      this._qy(ly)
    } else {
      this._qx(lx)
      this._qy(ly)
    }
  }
}
